/**
 * Avatar — shadcn/ui primitive (TruSpace).
 *
 * Radix-backed avatar with graceful image-load fallback. Used by the
 * app header user menu and anywhere a person/brand mark is shown.
 *
 * Anatomy:
 *   <Avatar>
 *     <AvatarImage src={url} alt="Jane Doe" />
 *     <AvatarFallback>JD</AvatarFallback>   // shown until/if image loads
 *   </Avatar>
 *
 * @see https://ui.shadcn.com/docs/components/avatar
 */
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import type { ComponentPropsWithoutRef } from 'react';
import { cn } from '@/lib/cn';

export function Avatar({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>) {
  return (
    <AvatarPrimitive.Root
      className={cn(
        'relative flex h-9 w-9 shrink-0 overflow-hidden rounded-full',
        className,
      )}
      {...props}
    />
  );
}

export function AvatarImage({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image
      className={cn('aspect-square h-full w-full object-cover', className)}
      {...props}
    />
  );
}

export function AvatarFallback({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      className={cn(
        'flex h-full w-full items-center justify-center rounded-full bg-muted text-sm font-medium text-muted-foreground',
        className,
      )}
      {...props}
    />
  );
}
